/**
 * Plot Drives Field Mapping
 * Maps plot drives field names to their display labels and units
 * These labels should match the frontend_label or name in groundworks_rates_mapping.json
 */

/**
 * Mapping of plot drives field names to their display labels
 */
export const PLOT_DRIVES_FIELD_LABELS: { [fieldName: string]: string } = {
  // Private Macadam
  'macadamLevelCompact': 'Level and compact formation',
  'macadamGeotextileLayer': 'Geotextile Layer',
  'macadamSubBase': 'xxxmm thick MOT Type 1',
  'macadamDbBase': 'xxmm DB  Base',
  'macadamDbBinder': 'xxmm DB Binder',
  'macadamSealingGrit': 'Sealing Grit',
  'macadamRemoveTimberEdgings': 'Remove Timber edgings',
  'macadamTack': 'Sweep & Tack',
  'macadamDbSurfaceCourse': 'xxmm DB Surface course',
  'macadamHraSurfaceCourse': 'xxmm HRA Surface course',
  'macadamSmaSurfaceCourse': 'xxmm SMA Surface course',

  // Private Block Paved
  'blockLevelCompact': 'Level and compact formation',
  'blockGeotextileLayer': 'Geotextile Layer',
  'block300mmAdditionalCapping': 'xxxmm thick MOT Type 1',
  'block250mmSubBase': 'xxmm DB Base',
  'block100mmDbBase': 'xxmm DB Binder',
  'blockDbBinder': 'Sealing Grit',
  'blockSubBase': 'xxxmm thick MOT Type 1',
  'blockBase': 'xxmm DB Base',
  'blockBinder': 'xxmm DB Binder',
  'blockSealingGrit': 'Sealing Grit',
  'blockSweep': 'Sweep',
  'blockPunctureMacadam': 'Puncture Macadam',
  'blockLayingCourse': 'xxmm sand Laying Course',
  'block60mmConcrete': '60mm Concrete paviors',
  'block80mmConcrete': '80mm Concrete paviors',

  // Permeable Paving
  'permeableLevelCompact': 'Level and compact formation',
  'permeableImpermeableGeotextileLayer': 'Terram Layer to Bottom and Sides permeable paving',
  'permeableDbBinderRunningCourse': 'xxmm DB binder Running course',
  'permeable10to63mmCleanAggregateSubBase': 'xxmm thick 63-10mm Sub Base - Varied Depths',
  'permeable5to20mmCleanAggregateSubBase': 'xxmm thick 5-20mm clean aggregate sub base',
  'permeableGeotextileLayer': 'Permeable Geotextile Layer',
  'permeablePunctureMacadam': 'Puncture Macadam',
  'permeable2to6mmLayingAggregate': 'xxmm thick 2-6mm laying Aggregate',
  'permeable60mmConcrete': '60mm Concrete paviors',
  'permeable80mmConcrete': '80mm Concrete paviors',

  // Shared Macadam
  'sharedMacadamLevelCompact': 'Level and compact formation',
  'sharedMacadamGeotextileLayer': 'Geotextile Layer',
  'sharedMacadamSubBase': 'xxxmm thick MOT Type 1',
  'sharedMacadamDbBase': 'xxmm DB  Base',
  'sharedMacadamDbBinder': 'xxmm DB Binder',
  'sharedMacadamSealingGrit': 'Sealing Grit',
  'sharedMacadamSweepTack': 'Sweep & Tack',
  'sharedMacadamDbSurfaceCourse': 'xxmm DB Surface course',
  'sharedMacadamHraSurfaceCourse': 'xxmm HRA Surface course',
  'sharedMacadamSmaSurfaceCourse': 'xxmm SMA Surface course',

  // Shared Block Paved
  'sharedBlockLevelCompact': 'Level and compact formation',
  'sharedBlockGeotextileLayer': 'Geotextile Layer',
  'sharedBlockSubBase': 'xxxmm thick MOT Type 1',
  'sharedBlockBase': 'xxmm DB Base',
  'sharedBlockBinder': 'xxmm DB Binder',
  'sharedBlockSealingGrit': 'Sealing Grit',
  'sharedBlockPunctureMacadam': 'Puncture Macadam',
  'sharedBlockLayingCourse': 'xxmm sand Laying Course',
  'sharedBlock60mmConcrete': '60mm Concrete paviors',
  'sharedBlock80mmConcrete': '80mm Concrete paviors',

  // Shared Permeable
  'sharedPermeableLevelCompact': 'Level and compact formation',
  'sharedPermeableImpermeableGeotextileLayer': 'Terram Layer to Bottom and Sides permeable paving',
  'sharedPermeableDbBinderRunningCourse': 'xxmm DB binder Running course',
  'sharedPermeable10to63mmCleanAggregateSubBase': 'xxmm thick 63-10mm Sub Base - Varied Depths',
  'sharedPermeable5to20mmCleanAggregateSubBase': 'xxmm thick 5-20mm clean aggregate sub base',
  'sharedPermeableGeotextileLayer': 'Permeable Geotextile Layer',
  'sharedPermeablePunctureMacadam': 'Puncture Macadam',
  'sharedPermeable2to6mmLayingAggregate': 'xxmm thick 2-6mm laying Aggregate',
  'sharedPermeable60mmConcrete': '60mm Concrete paviors',
  'sharedPermeable80mmConcrete': '80mm Concrete paviors',
  'sharedPermeable150x50mmPccEdgings': '150x50mm PCC Edgings',
  'sharedPermeable200x50mmPccEdgings': '200x50mm PCC Edgings',
  'sharedPermeableTimberEdgings': 'Timber Edgings',
  'sharedPermablePccKerbs': 'PCC Kerbs',
  'sharedPermeableKerbType1': 'xxxx Kerb',
  'sharedPermeableKerbType2': 'xxxx Kerb',
  'sharedPermeableKerbType3': 'xxxx Kerb',
  'sharedPermeableKeyKerb': 'Key Kerb',
  'sharedPermeableRecessedCF': 'Recessed C&F',
  'sharedPermeableAdjustIronwork': 'Adjust Ironwork',
  'sharedPermeableDriveDemo': 'Drive Demarcation',
  'sharedPermeableEdgingRemedials': 'Edging Remedials',
  'sharedPermeableKerbRemedials': 'Kerb Remedials',
};

/**
 * Mapping of plot drives field names to their units
 */
export const PLOT_DRIVES_FIELD_UNITS: { [fieldName: string]: string } = {
  // Private Macadam
  'macadamLevelCompact': 'm²',
  'macadamGeotextileLayer': 'm²',
  'macadamSubBase': 'm²',
  'macadamDbBase': 'm²',
  'macadamDbBinder': 'm²',
  'macadamSealingGrit': 'm²',
  'macadamRemoveTimberEdgings': 'm',
  'macadamTack': 'm²',
  'macadamDbSurfaceCourse': 'm²',
  'macadamHraSurfaceCourse': 'm²',
  'macadamSmaSurfaceCourse': 'm²',

  // Private Block Paved
  'blockLevelCompact': 'm²',
  'blockGeotextileLayer': 'm²',
  'block300mmAdditionalCapping': 'm²',
  'block250mmSubBase': 'm²',
  'block100mmDbBase': 'm²',
  'blockDbBinder': 'm²',
  'blockSubBase': 'm²',
  'blockBase': 'm²',
  'blockBinder': 'm²',
  'blockSealingGrit': 'm²',
  'blockSweep': 'm²',
  'blockPunctureMacadam': 'm²',
  'blockLayingCourse': 'm²',
  'block60mmConcrete': 'm²',
  'block80mmConcrete': 'm²',

  // Permeable Paving
  'permeableLevelCompact': 'm²',
  'permeableImpermeableGeotextileLayer': 'm²',
  'permeableDbBinderRunningCourse': 'm²',
  'permeable10to63mmCleanAggregateSubBase': 'm²',
  'permeable5to20mmCleanAggregateSubBase': 'm²',
  'permeableGeotextileLayer': 'm²',
  'permeablePunctureMacadam': 'm²',
  'permeable2to6mmLayingAggregate': 'm²',
  'permeable60mmConcrete': 'm²',
  'permeable80mmConcrete': 'm²',

  // Shared Macadam
  'sharedMacadamLevelCompact': 'm²',
  'sharedMacadamGeotextileLayer': 'm²',
  'sharedMacadamSubBase': 'm²',
  'sharedMacadamDbBase': 'm²',
  'sharedMacadamDbBinder': 'm²',
  'sharedMacadamSealingGrit': 'm²',
  'sharedMacadamSweepTack': 'm²',
  'sharedMacadamDbSurfaceCourse': 'm²',
  'sharedMacadamHraSurfaceCourse': 'm²',
  'sharedMacadamSmaSurfaceCourse': 'm²',

  // Shared Block Paved
  'sharedBlockLevelCompact': 'm²',
  'sharedBlockGeotextileLayer': 'm²',
  'sharedBlockSubBase': 'm²',
  'sharedBlockBase': 'm²',
  'sharedBlockBinder': 'm²',
  'sharedBlockSealingGrit': 'm²',
  'sharedBlockPunctureMacadam': 'm²',
  'sharedBlockLayingCourse': 'm²',
  'sharedBlock60mmConcrete': 'm²',
  'sharedBlock80mmConcrete': 'm²',

  // Shared Permeable
  'sharedPermeableLevelCompact': 'm²',
  'sharedPermeableImpermeableGeotextileLayer': 'm²',
  'sharedPermeableDbBinderRunningCourse': 'm²',
  'sharedPermeable10to63mmCleanAggregateSubBase': 'm²',
  'sharedPermeable5to20mmCleanAggregateSubBase': 'm²',
  'sharedPermeableGeotextileLayer': 'm²',
  'sharedPermeablePunctureMacadam': 'm²',
  'sharedPermeable2to6mmLayingAggregate': 'm²',
  'sharedPermeable60mmConcrete': 'm²',
  'sharedPermeable80mmConcrete': 'm²',
  'sharedPermeable150x50mmPccEdgings': 'm',
  'sharedPermeable200x50mmPccEdgings': 'm',
  'sharedPermeableTimberEdgings': 'm',
  'sharedPermablePccKerbs': 'm',
  'sharedPermeableKerbType1': 'm',
  'sharedPermeableKerbType2': 'm',
  'sharedPermeableKerbType3': 'm',
  'sharedPermeableKeyKerb': 'm',
  'sharedPermeableRecessedCF': 'Nr',
  'sharedPermeableAdjustIronwork': 'Nr',
  'sharedPermeableDriveDemo': 'Nr',
  'sharedPermeableEdgingRemedials': 'Nr',
  'sharedPermeableKerbRemedials': 'Nr',
};

/**
 * Get the display label for a plot drives field
 */
export function getPlotDrivesFieldLabel(fieldName: string): string | undefined {
  return PLOT_DRIVES_FIELD_LABELS[fieldName];
}

/**
 * Get the unit for a plot drives field
 */
export function getPlotDrivesFieldUnit(fieldName: string): string | undefined {
  return PLOT_DRIVES_FIELD_UNITS[fieldName];
}

/**
 * Reverse mapping: label to field name
 */
export const PLOT_DRIVES_REVERSE_MAPPING_LABEL: { [label: string]: string } = {};
Object.entries(PLOT_DRIVES_FIELD_LABELS).forEach(([fieldName, label]) => {
  PLOT_DRIVES_REVERSE_MAPPING_LABEL[label] = fieldName;
});
